/*
  customsirge_notify_me.js
  Purpose:  Behaviour for snippets/customsirge_notify_me.liquid — the sold-out
            "Notify me" popup. Opens the <dialog>, closes it on Escape, the
            close button or a backdrop click, posts the Shopify customer
            form in the background when no Klaviyo key is set, and
            (when a Klaviyo public key is configured) submits the email to
            Klaviyo's public Back-in-Stock endpoint.
  Spec:     .claude/knowledge/extraction/figma/sections/collection_product_grid/
            BRIEF.md → "## Client answers (2026-09-15)" Q8 — exact endpoint,
            headers and JSON:API body are copied verbatim from that answer.
  Owns:     window.Customsirge components.notify_me; no CSS namespace of its
            own (see the snippet's own "WHY NO CSS FILE" note).
  Loaded:   by snippets/customsirge_notify_me.liquid itself, once per render
            (see the snippet header — repeating this <script defer> tag is the
            same accepted tradeoff as its inline {% style %} block: identical
            requests the browser fetches once and reuses from cache).
  Owner:    Foundation B kit builder (collection_product_grid) — 2026-09-15
  Updated:  figma-fixer(collection_product_grid) loop 1, 2026-09-15 — CPG-01,
            CPG-10 (see discrepancies.json for this run).
*/

(function () {
  'use strict';

  if (!window.Customsirge || !window.Customsirge.register) return;

  var ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var REVISION = '2024-10-15';

  Customsirge.register('notify_me', {
    selector: '[data-customsirge-notify]',
    init: function (el) {
      var trigger = el.querySelector('[data-customsirge-notify-trigger]');
      var panel = el.querySelector('[data-customsirge-notify-panel]');
      var form = el.querySelector('[data-customsirge-notify-form]');
      var shopifyForm = panel ? panel.querySelector('form.customsirge-notify__form:not([data-customsirge-notify-form])') : null;
      var status = el.querySelector('[data-customsirge-notify-status]');
      var closeButton = el.querySelector('[data-customsirge-notify-close]');
      if (!trigger || !panel || typeof panel.showModal !== 'function') return;

      // 2026-10-19: the panel is a <dialog> popup (was an inline drop-down).
      // showModal() puts it in the top layer, traps focus and handles Escape.
      var open = function () {
        if (panel.open) return;
        panel.showModal();
        trigger.setAttribute('aria-expanded', 'true');
        var input = panel.querySelector('input[type="email"]');
        if (input && !input.closest('[hidden]')) input.focus({ preventScroll: true });
      };

      var close = function () {
        if (panel.open) panel.close();
      };

      var onClose = function () {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus({ preventScroll: true });
      };

      var onTriggerClick = function () {
        open();
      };

      // A click on the dialog element itself (not its content) is the backdrop.
      var onPanelClick = function (event) {
        if (event.target === panel) close();
      };

      trigger.addEventListener('click', onTriggerClick);
      panel.addEventListener('click', onPanelClick);
      panel.addEventListener('close', onClose);
      if (closeButton) closeButton.addEventListener('click', close);

      // No Klaviyo key: post Shopify's customer form in the background so the
      // popup can thank the shopper without a page reload. If Shopify answers
      // with a challenge page (bot protection), fall back to a normal submit.
      var onShopifySubmit = null;
      if (shopifyForm) {
        onShopifySubmit = function (event) {
          var emailInput = shopifyForm.querySelector('input[type="email"]');
          if (!emailInput || !emailInput.value.trim()) return;
          event.preventDefault();
          var submitButton = shopifyForm.querySelector('[type="submit"]');
          if (submitButton) submitButton.disabled = true;
          setStatus(status, el.dataset.msgSending || 'Sending…');
          fetch(shopifyForm.action, { method: 'POST', body: new FormData(shopifyForm), credentials: 'same-origin' })
            .then(function (response) {
              if (/\/challenge/.test(response.url)) {
                shopifyForm.removeEventListener('submit', onShopifySubmit);
                shopifyForm.submit();
                return;
              }
              if (response.ok) {
                setStatus(status, el.dataset.msgSuccess || "You're on the list. We'll email you when it's back.");
                shopifyForm.hidden = true;
              } else {
                setStatus(status, el.dataset.msgError || 'Something went wrong. Please try again.');
              }
            })
            .catch(function () {
              setStatus(status, el.dataset.msgError || 'Something went wrong. Please try again.');
            })
            .finally(function () {
              if (submitButton) submitButton.disabled = false;
            });
        };
        shopifyForm.addEventListener('submit', onShopifySubmit);
      }

      var onSubmit = null;
      if (form) {
        onSubmit = function (event) {
          event.preventDefault();
          var emailInput = form.querySelector('input[name="email"]');
          var email = emailInput ? emailInput.value.trim() : '';
          if (!email) return;

          var submitButton = form.querySelector('[type="submit"]');
          var companyId = el.dataset.klaviyoKey;
          var variantId = el.dataset.variantId;
          // CPG-10: these three strings come from the snippet's own render
          // args (with the same defaults) via data-msg-*, not hardcoded here.
          var msgSending = el.dataset.msgSending || 'Sending…';
          var msgSuccess = el.dataset.msgSuccess || "You're on the list — we'll email you when it's back.";
          var msgError = el.dataset.msgError || 'Something went wrong — please try again.';

          if (submitButton) submitButton.disabled = true;
          setStatus(status, msgSending);

          fetch(ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/vnd.api+json',
              revision: REVISION,
            },
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  profile: { data: { type: 'profile', attributes: { email: email } } },
                  channels: ['EMAIL'],
                },
                relationships: {
                  variant: {
                    data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId },
                  },
                },
              },
            }),
          })
            .then(function (response) {
              if (response.status === 202) {
                setStatus(status, msgSuccess);
                form.hidden = true;
              } else {
                setStatus(status, msgError);
              }
            })
            .catch(function () {
              setStatus(status, msgError);
            })
            .finally(function () {
              if (submitButton) submitButton.disabled = false;
            });
        };
        form.addEventListener('submit', onSubmit);
      }

      return function () {
        trigger.removeEventListener('click', onTriggerClick);
        panel.removeEventListener('click', onPanelClick);
        panel.removeEventListener('close', onClose);
        if (closeButton) closeButton.removeEventListener('click', close);
        if (shopifyForm && onShopifySubmit) shopifyForm.removeEventListener('submit', onShopifySubmit);
        if (form && onSubmit) form.removeEventListener('submit', onSubmit);
      };
    },
  });

  function setStatus(statusEl, message) {
    if (!statusEl) return;
    statusEl.textContent = message;
    statusEl.hidden = !statusEl.textContent;
  }
})();
